import "@src/pages/formDrawer/drawer.css";
import "./proofOfAttendanceDrawer.css";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Drawer, Image, Skeleton, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { getDutyProofQuery } from "@src/util/query/dutyProof.query";
import { getScheduleCheckCallQuery } from "@src/util/mutation/scheduleChecks.mutation";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import { BACKEND_URL } from "@src/config/apollo";

// Proof of attendance for one duty (Schedule -> completed duty -> Proof of
// attendance): the customer portal's duty drawer, ported as-is (wfc-customer
// components/dutyDrawer/dutyDrawer.jsx + .css) - who and when, its status, clock
// in/out, hours, check calls, every clock / check call / checkpoint with the
// selfie and a map of where it was done (geofence drawn, inside / outside), and
// the full duty report. Keep the two in step. The header is this app's own drawer
// header (teal bar, white title, round close - formDrawer/drawer.css), which stays
// put while the body scrolls.

const STATE = {
    missedClockIn: { label: "Missed clock-in", color: "#dc2626", bg: "#fef2f2" },
    noShow:        { label: "No-show",         color: "#b91c1c", bg: "#fef2f2" },
    unassigned:    { label: "Needs officer",   color: "#e8590c", bg: "#fff4ed" },
    onDuty:        { label: "On duty",         color: "#16a34a", bg: "#f0fdf4" },
    awaiting:      { label: "Awaiting clock-in", color: "#0091ae", bg: "#e5f5f8" },
    upcoming:      { label: "Upcoming",        color: "#516f90", bg: "#f5f8fa" },
    completed:     { label: "Completed",       color: "#126e7d", bg: "#e5f5f8" },
};

const EVENT_STATE = {
    onTime:  { label: "On time", color: "#16a34a" },
    late:    { label: "Late",    color: "#e8590c" },
    missed:  { label: "Missed",  color: "#dc2626" },
    pending: { label: "Due",     color: "#7c98b6" },
};

const googleMapsUrl = (l) => `https://www.google.com/maps?q=${l.lat},${l.lng}`;

const timeRange = (start, end) => {
    const s = dayjs(start), e = dayjs(end);
    if (!s.isValid()) return "";
    if (!e.isValid()) return s.format("HH:mm");
    return `${s.format("HH:mm")}–${e.format("HH:mm")}${e.isAfter(s, "day") ? " (+1)" : ""}`;
};

const formatHours = (h) => {
    if (h === null || h === undefined) return "—";
    const total = Math.round(Number(h) * 60);
    const mins = total % 60;
    return mins ? `${Math.floor(total / 60)}h ${String(mins).padStart(2, "0")}m` : `${Math.floor(total / 60)}h`;
};

const clockText = (c, emptyText) => {
    if (!c) return emptyText;
    return [dayjs(c.at).format("HH:mm"), c.booked && "booked", c.late && "late", c.automated && "automatic"].filter(Boolean).join(" · ");
};

const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
};

// The full duty report PDF (wfc-server routes/dutyDetailReport.route.ts), signed in
// with the web session cookie. Only once the duty is completed.
export const downloadFullDutyReport = async (scheduleId) => {
    const res = await fetch(`${BACKEND_URL}/reports/duty/${scheduleId}/pdf`, { credentials: "include" });
    if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.message || `Request failed (${res.status})`);
    }
    saveBlob(await res.blob(), `duty-detail-${scheduleId}.pdf`);
};

const csvValue = (v) => {
    const str = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const Pill = ({ label, color, bg }) => (
    <span className="dd-pill" style={{ color, borderColor: color, background: bg || "#fff" }}>{label}</span>
);

const MiniMap = ({ location, map, geofence }) => (
    <div className="dd-mapbox">
        <a href={googleMapsUrl(location)} target="_blank" rel="noreferrer" title="Open in Google Maps">
            {map ? <img src={map} alt="Map of where this was done" /> : <span className="dd-map-empty">Map unavailable</span>}
        </a>
        {geofence && <div className={`dd-geo dd-geo-${geofence.status}`}>{geofence.text}</div>}
        <a className="dd-map" href={googleMapsUrl(location)} target="_blank" rel="noreferrer">Open in Google Maps ›</a>
    </div>
);

const Fact = ({ label, children }) => (
    <div className="dd-fact">
        <span className="dd-fact-label">{label}</span>
        <span className="dd-fact-value">{children}</span>
    </div>
);

export const ProofOfAttendanceDrawer = ({ scheduleId, source = "schedule", onClose }) => {
    const dispatch = useDispatch();
    const [busy, setBusy] = useState(null); // "pdf" | "csv"
    const notifyError = (message) => dispatch(setNotification({ error: true, notificationState: true, message }));

    const { data, loading } = useQuery(getDutyProofQuery, {
        variables: { source, id: scheduleId },
        skip: !scheduleId,
        fetchPolicy: "network-only",
    });
    const [fetchCalls] = useLazyQuery(getScheduleCheckCallQuery, { fetchPolicy: "network-only" });

    const proof = data?.getDutyProof?.response;
    const duty = proof?.row;
    const isMobile = source === "mobileService";
    const isCompleted = duty?.state === "completed" || !!proof?.isCompleted;
    const meta = STATE[duty?.state] || (isCompleted ? STATE.completed : STATE.upcoming);
    const officer = duty?.officer || (duty?.assigned || duty?.clockIn ? "Supplier officer" : "Not yet assigned");

    const busyIcon = <Spin indicator={<LoadingOutlined style={{ fontSize: 14, color: "#0091ae" }} spin />} />;

    const downloadPdf = async () => {
        setBusy("pdf");
        try { await downloadFullDutyReport(scheduleId); } catch (err) { notifyError(err?.message || "Failed to download the duty report."); } finally { setBusy(null); }
    };

    // every clock in/out, check call, checkpoint and task, due vs done, with GPS - same CSV as the portal
    const downloadCsv = async () => {
        setBusy("csv");
        try {
            const { data: callData } = await fetchCalls({ variables: { siteId: proof?.siteId, scheduleId } });
            const calls = (callData?.getScheduleCheckCalls?.response || []).slice()
                .sort((a, b) => dayjs(a?.requiredAt).valueOf() - dayjs(b?.requiredAt).valueOf());
            if (!calls.length) { notifyError("No activity logged for this duty yet."); return; }
            const rows = [["Call Type", "Label", "Expected Date/Time", "Actual Date/Time", "Status", "Lat", "Lng"], ...calls.map((c) => [
                c?.callType, c?.label,
                c?.requiredAt ? dayjs(c.requiredAt).format("DD/MM/YYYY HH:mm") : "",
                c?.actualDate ? dayjs(c.actualDate).format("DD/MM/YYYY HH:mm") : "",
                c?.pending ? "Pending" : (c?.late ? "Late" : "On time"),
                c?.actualLocation?.latitude ?? "", c?.actualLocation?.longitude ?? "",
            ])];
            saveBlob(new Blob(["﻿" + rows.map((r) => r.map(csvValue).join(",")).join("\r\n")], { type: "text/csv;charset=utf-8;" }), `duty-report-${scheduleId}.csv`);
        } catch (err) {
            notifyError("Failed to download the activity log.");
        } finally {
            setBusy(null);
        }
    };

    return (
        <Drawer
            open={!!scheduleId}
            onClose={onClose}
            width={Math.min(560, window.innerWidth)}
            title="Proof of attendance"
            closable
            closeIcon={<FontAwesomeIcon icon={faClose} className="close-icon" style={{marginRight: '30px'}} />}
            className="dd-drawer"
            destroyOnClose
            zIndex={1100}
        >
            {loading && !proof ? <div className="dd-loading"><Skeleton active paragraph={{ rows: 10 }} /></div> : proof && (
                <div className="dd">
                    <div className="dd-head">
                        <div className="dd-head-main">
                            <div className="dd-eyebrow">{isMobile ? "Mobile patrol" : "Guarding"} · {dayjs(proof.start).format("ddd D MMM YYYY")}</div>
                            <h2 className="dd-title">{proof.siteName}</h2>
                            <div className="dd-sub">{timeRange(proof.start, proof.end)} · {duty?.dutyType || (isMobile ? "Mobile patrol" : "Guarding")}</div>
                        </div>
                    </div>

                    <div className="dd-status">
                        <Pill label={meta.label} color={meta.color} bg={meta.bg} />
                        <span className="dd-officer">{officer}</span>
                    </div>

                    <div className="dd-facts">
                        <Fact label={isMobile ? "Started" : "Clock in"}>{clockText(duty?.clockIn, "—")}</Fact>
                        <Fact label={isMobile ? "Finished" : "Clock out"}>{clockText(duty?.clockOut, "—")}</Fact>
                        <Fact label="Booked">{formatHours(duty?.hours)}</Fact>
                        {/* a mobile job is a visit, not a shift */}
                        <Fact label="On duty">{isMobile ? "Not applicable" : formatHours(duty?.actualHours)}</Fact>
                        {!isMobile && <Fact label="Check calls">{duty?.checkCalls?.due ? `${Math.min(duty.checkCalls.done, duty.checkCalls.due)} of ${duty.checkCalls.due}` : "None due"}</Fact>}
                        {!isMobile && <Fact label="Checkpoints">{duty?.checkpoints?.due ? `${Math.min(duty.checkpoints.done, duty.checkpoints.due)} of ${duty.checkpoints.due}` : "None due"}</Fact>}
                    </div>

                    <section className="dd-section">
                        <h3>Proof of attendance</h3>
                        {isMobile && proof?.route && (
                            <div className="dd-route">
                                <a href={googleMapsUrl(proof.route.finish)} target="_blank" rel="noreferrer" title="Open the finish point in Google Maps">
                                    <img src={proof.route.map} alt="The route recorded on the app" />
                                </a>
                                <div className="dd-route-note"><span className="dd-route-start" /> Start · <span className="dd-route-line" /> route recorded on the app ({proof.route.points} point{proof.route.points === 1 ? "" : "s"}) · pin = finish</div>
                            </div>
                        )}
                        {!proof.events?.length ? (
                            <div className="dd-empty">Nothing logged for this duty yet.</div>
                        ) : (
                            <ol className="dd-timeline">
                                {proof.events.map((e, i) => {
                                    const es = isMobile ? EVENT_STATE.onTime : (EVENT_STATE[e.state] || EVENT_STATE.pending);
                                    return (
                                        <li key={e._id || i} className="dd-event">
                                            <span className="dd-dot" style={{ background: es.color }} />
                                            <div className="dd-event-main">
                                                <div className="dd-event-top">
                                                    <span className="dd-event-type">{e.label && e.label !== e.type ? `${e.type} · ${e.label}` : e.type}</span>
                                                    {!isMobile && <span className="dd-event-state" style={{ color: es.color }}>{es.label}</span>}
                                                </div>
                                                <div className="dd-event-sub">
                                                    {e.dueAt && <>Due {dayjs(e.dueAt).format("HH:mm")}</>}
                                                    {e.at && <>{e.dueAt ? " · " : ""}{e.booked ? "Booked" : "Done"} {dayjs(e.at).format("HH:mm")}</>}
                                                    {e.automated && " · automatic, no phone check"}
                                                    {e.booked && " · not recorded on the app"}
                                                </div>
                                                {(e.selfie || e.photo || e.location) && (
                                                    <div className="dd-evidence">
                                                        {(e.selfie || e.photo) && (
                                                            <div className="dd-photos">
                                                                {e.selfie && <Image src={e.selfie} width={84} height={104} className="dd-thumb" alt="Officer selfie" />}
                                                                {e.photo && <Image src={e.photo} width={84} height={104} className="dd-thumb" alt="Photo" />}
                                                            </div>
                                                        )}
                                                        {e.location && <MiniMap location={e.location} map={e.map} geofence={e.geofence} />}
                                                    </div>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ol>
                        )}
                        {proof.mapAttribution && <div className="dd-attribution">{proof.mapAttribution}</div>}
                    </section>

                    {!isMobile && (
                        <section className="dd-section">
                            <h3>Reports</h3>
                            <div className="dd-actions">
                                <button
                                    type="button"
                                    className="dd-btn"
                                    disabled={!isCompleted || !!busy}
                                    onClick={downloadPdf}
                                    title={isCompleted ? "" : "Available once the duty is completed"}
                                >
                                    {busy === "pdf" ? busyIcon : isCompleted ? "Full duty report (PDF)" : "Full report once completed"}
                                </button>
                                <button type="button" className="dd-btn" disabled={!!busy} onClick={downloadCsv}>
                                    {busy === "csv" ? busyIcon : "Activity log (CSV)"}
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            )}
        </Drawer>
    );
};
